export type Branding = {
  name: string;
  shortName: string;
  description: string;
  domain: string;
  useAnalyticsTools: boolean;
  userGuide: boolean;
  assetPath?: string;
  defaultShardEnv?: string;
  fixedPlanCountry?: string;
  downloadPage: {
    showDownloadPage: boolean;
    showStealthDownload: boolean;
    showStandardDownload: boolean;
    showInstallationGuide: boolean;
    showMacDownload: boolean;
    showWindowsDownload: boolean;
    showLinuxDownload: boolean;
  };
  api: {
    originUrl: string;
  };
  passwordPolicy: {
    minLength: number;
    maxLength: number;
  };
  urls: {
    privacyPolicyUrl: string;
    termsAndConditionsUrl: string;
    documentationUrl?: string;
    requestOnboardingUrl?: string;
    raiseATicketUrl?: string;
    feedbackUrl?: string;
  };
  theme: {
    primary: string;
    secondary: string;
    sidebar_background: string;
  };
  v4Enterprise?: {
    enabled: boolean;
    platformUrl: string;
  };
};

let brandingCache: Branding | null = null;

export async function loadBranding(): Promise<Branding> {
  if (brandingCache) return brandingCache;

  // 1️⃣ Injected <script> tag (production)
  const tag = document.getElementById("brand-config");
  if (tag?.textContent) {
    try {
      brandingCache = JSON.parse(tag.textContent);
      return brandingCache;
    } catch (e) {
      console.error("Invalid brand config JSON", e);
    }
  }

  // 2️⃣ Local fallback (for dev)
  try {
    const res = await fetch("/branding/local/config.json");
    if (res.ok) {
      brandingCache = await res.json();
      return brandingCache;
    }
  } catch (e) {
    console.warn("Failed to fetch local branding", e);
  }

  // 3️⃣ Default
  brandingCache = {
    name: "Local Dev",
    shortName: "LD",
    description: "Local development environment",
    domain: "locallocaldev.ai",
    useAnalyticsTools: true,
    userGuide: true,
    downloadPage: {
      showDownloadPage: true,
      showStealthDownload: true,
      showStandardDownload: true,
      showInstallationGuide: true,
      showMacDownload: true,
      showWindowsDownload: true,
      showLinuxDownload: true,
    },
    api: {
      originUrl: "https://origin.in.we360.ai/",
    },
    passwordPolicy: {
      minLength: 8,
      maxLength: 64,
    },
    urls: {
      privacyPolicyUrl: "https://locallocaldev.ai/privacy-policy",
      termsAndConditionsUrl: "https://locallocaldev.ai/terms-and-conditions",
      documentationUrl: "https://support.locallocaldev.ai/en/",
      requestOnboardingUrl:
        "https://calendly.com/locallocaldevai_support/support-and-onboarding?utm_source=Product&utm_medium=WebApplication&utm_term=RequestOnboarding&utm_campaign=HelpCenter",
      raiseATicketUrl: "https://support.locallocaldev.ai/en/tickets-portal",
      feedbackUrl: "https://feedback.locallocaldev.ai/b/zv9xr6v1/feature-ideas",
    },
    theme: {
      primary: "#1e85d8",
      secondary: "#f0f0f0",
      sidebar_background: "#0e89cc",
    },
  };
  return brandingCache;
}

export function getBranding(): Branding | null {
  return brandingCache;
}
